<template>
    <div class="task-complete-modal-goal">
        <GoalModal
            data-qa="task-complete-modal-goal"
            :is-open="isOpen"
            :goal-tip-link="$t('funnel.campaign.task.complete.more.link')"
            :goal-tip-text="$t('funnel.campaign.task.complete.pro.tip')"
            :summary-text="$t('funnel.campaign.task.complete.summary')"
            alert-type="critical"
            :show-warning="showConfigurationWarning"
            :warning-text="$t('funnel.campaign.task.complete.warning')"
            size="lg"
            :title="$t('funnel.campaign.task.complete.title')"
            :cell="cell"
            :app-name="appName"
            :cell-type="$options.GOAL_TASK_COMPLETED"
            :show-save="cellUpdateEnabled"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section v-if="validRuleDisplay" class="completion-rule">
                    <div
                        v-for="(rule, index) in taskCompleteRuleConfiguration"
                        :key="index"
                        :data-qa="`task-complete-rule-${index}`"
                    >
                        <TaskCompleteRule
                            :id="index"
                            :key="index"
                            :title="rule.title"
                            :task-options="availableTasksForGoal"
                            :available-tasks-and-outcomes="availableTasksAndOutcomes"
                            :unavailable-task-ids="selectedTasks"
                            :configuration="rule.configuration"
                            @task-selection="updateTaskSelection"
                            @delete-rule="deleteRule"
                            @configuration-warning="triggerConfigurationWarning"
                            @update-configuration="updateRuleConfiguration"
                        />
                    </div>
                </section>
                <section class="add-rule">
                    <DsPopover :class="anyPopoverClass" position="right">
                        <template #reference>
                            <DsIconButton
                                name="add-fill"
                                data-qa="add-new-any-rule"
                                :disabled="!isAnyAchievementType"
                                :aria-label="$t('funnel.campaign.task.complete.or')"
                                @click="addOrRule"
                            />
                            <label :class="anyAchievementTypeClass">{{ $t('funnel.campaign.task.complete.or') }}</label>
                        </template>
                        <template #default>
                            <div>
                                {{ $t('funnel.campaign.task.complete.disabled') }}
                            </div>
                        </template>
                    </DsPopover>
                </section>
                <section class="add-rule">
                    <DsPopover :class="allPopoverClass" position="right">
                        <template #reference>
                            <DsIconButton
                                name="add-fill"
                                data-qa="add-new-all-rule"
                                :disabled="!isAllAchievementType"
                                :aria-label="$t('funnel.campaign.task.complete.and')"
                                @click="addAndRule"
                            />
                            <label :class="allAchievementTypeClass">{{ $t('funnel.campaign.task.complete.and') }}</label>
                        </template>
                        <template #default>
                            <div>
                                {{ $t('funnel.campaign.task.complete.disabled') }}
                            </div>
                        </template>
                    </DsPopover>
                </section>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import TaskCompleteRule from '../taskCompleteRule/TaskCompleteRule.vue';
import { GOAL_TASK_COMPLETED, PROCESS_CREATE_TASK } from '../../../utils/editor.constants';
import {
    areIncomingFlowsValidForCell,
    getValidIncomingFlows,
} from '../../../utils/cell/automation-cell-utils';

export const ACHIEVEMENT_TYPE = {
    ANY: 'any',
    ALL: 'all',
};

export default {
    GOAL_TASK_COMPLETED,
    PROCESS_CREATE_TASK,
    ACHIEVEMENT_TYPE,
    components: {
        GoalModal,
        TaskCompleteRule,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
            }),
        },
        funnelId: {
            type: Number,
            required: true,
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            achievementType: '',
            taskIds: [],
            taskCompleteRuleConfiguration: [],
            availableTasksAndOutcomes: [],
            availableTasksForGoal: [],
            selectedTasks: [],
            validRuleDisplay: true,
            showConfigurationWarning: false,
            cellUpdateEnabled: false,
        };
    },

    computed: {
        appName() {
            return this.appInfo?.appName;
        },

        validIncomingSequence() {
            return this.availableTasksAndOutcomes?.length > 0;
        },

        taskOptionsAvailable() {
            return this.taskCompleteRuleConfiguration?.length < this.availableTasksForGoal?.length;
        },

        isAnyAchievementType() {
            return (this.canChangeAchievementType || this.achievementType === ACHIEVEMENT_TYPE.ANY) && this.taskOptionsAvailable;
        },

        isAllAchievementType() {
            return (this.canChangeAchievementType || this.achievementType === ACHIEVEMENT_TYPE.ALL) && this.taskOptionsAvailable;
        },

        canChangeAchievementType() {
            return this.achievementType === '' || this.taskCompleteRuleConfiguration?.length === 1;
        },

        allAchievementTypeClass() {
            return this.isAllAchievementType ? '' : 'disabled';
        },

        anyAchievementTypeClass() {
            return this.isAnyAchievementType ? '' : 'disabled';
        },

        anyPopoverClass() {
            return this.isAnyAchievementType ? 'popover-disabled' : 'popover-enabled';
        },

        allPopoverClass() {
            return this.isAllAchievementType ? 'popover-disabled' : 'popover-enabled';
        },
    },

    mounted() {
        this.validateGoal();
        this.getSequenceWithTasksConnected();
        this.loadConfig();
    },

    methods: {
        validateGoal() {
            const validationStatus = areIncomingFlowsValidForCell(this.cell, this.cells);

            if (!validationStatus.isValid) {
                this.$error({
                    message: this.$t(validationStatus.messageKey),
                    bottom: true,
                });
                this.closeModal();
            }
        },

        updateConfig() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.achievementType = this.achievementType !== '' ? this.achievementType : ACHIEVEMENT_TYPE.ALL;
            cell.value.taskIds = this.taskIds.filter((element) => element !== '');

            this.$emit('update-cell-value', { cell });
        },

        closeModal() {
            this.$emit('close-modal', GOAL_TASK_COMPLETED);
        },

        addOrRule() {
            this.achievementType = ACHIEVEMENT_TYPE.ANY;
            this.addNewRule(this.$t('funnel.campaign.task.complete.lower.or'), {});
        },

        addAndRule() {
            this.achievementType = ACHIEVEMENT_TYPE.ALL;
            this.addNewRule(this.$t('funnel.campaign.task.complete.lower.and'), {});
        },

        getSequenceWithTasksConnected() {
            this.availableTasksAndOutcomes = this.getAvailableTasksAndOutcomes();
            this.loadAvailableTaskOptions();
        },

        getValidIncomingFlowsForKonva(response) {
            const validIncomingFlows = getValidIncomingFlows(this.cell, this.cells);

            validIncomingFlows.forEach((flow) => {
                response.push({
                    id: `${flow.id}`,
                    name: `${flow.value.taskTitle}`,
                    outcomeIds: `${flow.value.taskOutcome}`,
                });
            });
        },

        getIncomingFlows(response) {
            this.getValidIncomingFlowsForKonva(response);
        },

        getAvailableTasksAndOutcomes() {
            const response = [];

            this.getIncomingFlows(response);

            return response;
        },

        loadAvailableTaskOptions() {
            if (!this.validIncomingSequence) {
                return;
            }

            this.availableTasksForGoal = this.availableTasksAndOutcomes.map((task) => (
                {
                    value: task.id,
                    label: task.name,
                }));
        },

        loadConfig() {
            const { value } = this.cell;

            if (!value) {
                return;
            }

            if (value.taskIds && value.taskIds !== '') {
                this.taskIds = [...value.taskIds];
                this.achievementType = value.achievementType;

                const configuredRules = this.processConfiguration(this.taskIds);
                const sanitizedRules = this.validatePreviousConfig(configuredRules);

                this.processRules(sanitizedRules);
            } else {
                this.addNewRule(this.$t('funnel.campaign.task.complete.when'), {});
            }
        },

        addNewRule(ruleLabel, configuration) {
            this.taskCompleteRuleConfiguration.push(this.getNewRule(ruleLabel, configuration));

            if (this.taskIds?.length < this.taskCompleteRuleConfiguration?.length) {
                this.taskIds = [...this.taskIds, ''];
            }
        },

        removeRule(rulePosition) {
            if (rulePosition >= 0 && rulePosition < this.taskCompleteRuleConfiguration.length) {
                this.taskCompleteRuleConfiguration.splice(rulePosition, 1);
            }

            if (rulePosition >= 0 && rulePosition < this.taskIds.length) {
                this.taskIds.splice(rulePosition, 1);
            }
        },

        getNewRule(title, configuration) {
            return {
                title,
                configuration,
            };
        },

        deleteRule({ rulePosition, taskId }) {
            this.validRuleDisplay = false;
            this.removeRule(rulePosition);

            if (this.taskCompleteRuleConfiguration.length > 0) {
                this.resetLabelForFirstRule();
            } else {
                this.addNewRule(this.$t('funnel.campaign.task.complete.when'), {});
            }

            this.updateTaskSelection({ value: '', oldValue: taskId });
            this.$nextTick(() => {
                this.validRuleDisplay = true;
                this.setCellUpdateEnabled();
            });
        },

        resetLabelForFirstRule() {
            this.taskCompleteRuleConfiguration[0].title = this.$t('funnel.campaign.task.complete.when');
        },

        triggerConfigurationWarning({ rulePosition, taskId, outcomeIds }) {
            this.setConfigurationValue(rulePosition, taskId, outcomeIds);
            this.showConfigurationWarning = true;
        },

        updateRuleConfiguration({ rulePosition, taskId, outcomeIds }) {
            this.setConfigurationValue(rulePosition, taskId, outcomeIds);
            this.setConfigurationRule(rulePosition, taskId, outcomeIds);
        },

        setConfigurationValue (rulePosition, taskId, outcomeIds) {
            this.cellUpdateEnabled = false;

            if (rulePosition >= 0 && rulePosition < this.taskIds.length) {
                const newRule = outcomeIds?.length > 0
                    ? `${taskId}:${outcomeIds.join(',')}`
                    : `${taskId || ''}`;

                this.taskIds[rulePosition] = newRule;
                this.setCellUpdateEnabled();
            }
        },

        setCellUpdateEnabled () {
            this.cellUpdateEnabled = this.taskIds.some((item) => item !== '');
        },

        setConfigurationRule (rulePosition, taskId, outcomeIds) {
            if (rulePosition >= 0 && rulePosition < this.taskCompleteRuleConfiguration.length) {
                this.taskCompleteRuleConfiguration[rulePosition].configuration = {
                    taskId,
                    taskOutcomeIds: outcomeIds,
                };
            }
        },

        updateTaskSelection({ value, oldValue }) {
            if (oldValue && oldValue !== '') {
                this.selectedTasks = this.selectedTasks.filter((element) => element !== oldValue);
            }

            if (value && value !== '') {
                if (!this.selectedTasks.includes(value)) {
                    this.selectedTasks = [...this.selectedTasks, value];
                }
            }
        },

        validatePreviousConfig(configuration) {
            const tasksSet = new Set(this.availableTasksForGoal.map((item) => `${item.value}`));

            if (configuration && !configuration.every((item) => tasksSet.has(item.taskId))) {
                this.taskIds = this.taskIds.filter((element) => {
                    const [taskId] = element.split(':');

                    return tasksSet.has(taskId);
                });

                this.showConfigurationWarning = true;
                this.cellUpdateEnabled = true;

                return configuration.filter((item) => tasksSet.has(item.taskId));
            }

            return configuration;
        },

        processConfiguration(configuration) {
            return configuration.map((item) => {
                const [taskId, taskOutcomeIdsString] = item.split(':');
                const taskOutcomeIds = taskOutcomeIdsString ? taskOutcomeIdsString.split(',') : [];

                return {
                    taskId,
                    taskOutcomeIds,
                };
            });
        },

        processRules(configuredRules) {
            if (configuredRules?.length > 0) {
                for (const rule of configuredRules) {
                    const label = this.isAnyAchievementType
                        ? this.$t('funnel.campaign.task.complete.lower.or')
                        : this.$t('funnel.campaign.task.complete.lower.and');

                    this.addNewRule(label, rule);
                    this.resetLabelForFirstRule();
                }
            } else {
                this.addNewRule(this.$t('funnel.campaign.task.complete.when'), {});
            }
        },
    },
};
</script>
<style lang="scss" scoped>
div.task-complete-modal-goal section {
    margin-left: - px-to-rem(24);
    margin-right: - px-to-rem(24);
}
div.task-complete-modal-goal section.completion-rule {
    border-bottom: solid 1px $color-gray-400;
}
div.task-complete-modal-goal section.add-rule {
    border-bottom: solid 1px $color-gray-400;
    margin-top: $spacing-150;
    padding-bottom: $spacing-150;
    margin-bottom: $spacing-200;
}
div.task-complete-modal-goal section.add-rule label {
    color: $color-blue-700;
}

div.task-complete-modal-goal section.add-rule label.disabled {
    color: $color-gray-600;
}
</style>

<style lang="scss">
div.task-complete-modal-goal div.modal-body.has-header  div.goal-summary {
    margin-top: -$spacing-200;
}
div.task-complete-modal-goal section.add-rule svg {
    color: $color-blue-700;
    margin-left: px-to-rem(38);
}
div.task-complete-modal-goal section.add-rule svg:hover {
    color: $color-blue-900;
}
div.task-complete-modal-goal section.add-rule button[disabled] svg,
div.task-complete-modal-goal section.add-rule button[disabled] svg:hover {
    color: $color-gray-600;
}
div.task-complete-modal-goal section.add-rule div.popover-disabled section {
    display: none;
}

div.task-complete-modal-goal section.add-rule div.popover-enabled section {
    padding: $spacing-200 $spacing-200;
    font-size: $font-size-md;
    font-weight: 400;
    line-height: $line-height-lg;
    width: px-to-rem(292);
}
</style>
